/**
 * Capa de fetch tipada para los endpoints de aceptación de invitaciones.
 *
 * Todos los endpoints están bajo /api/auth/invitation/* y requieren:
 *   - credentials: "include"
 *   - X-Requested-With: "portal-dashboard" (CSRF defense en POSTs)
 *
 * El módulo es independiente de console-api.ts y platform-api.ts para evitar
 * colisiones con cambios paralelos de otros agentes.
 *
 * Referencia: docs/DESIGN_invite_accept_flow.md (endpoints F2)
 */

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:6787";

// ---------------------------------------------------------------------------
// Helpers de fetch
// ---------------------------------------------------------------------------

async function invitationPost<T>(path: string, body: Record<string, string>): Promise<Response> {
  return fetch(`${API_BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "portal-dashboard",
    },
    body: JSON.stringify(body),
  });
}

// ---------------------------------------------------------------------------
// Tipos de respuesta — shapes del worker
// ---------------------------------------------------------------------------

/**
 * Modos posibles devueltos por POST /api/auth/invitation/preview.
 *
 * - "signup-required"  → el email de la invitación no tiene cuenta: se debe crear
 * - "login-required"   → el email existe pero no hay sesión activa: magic link
 * - "ready"            → hay sesión activa y el email coincide: aceptar directamente
 * - "wrong-account"    → hay sesión activa pero con un email diferente
 * - "invalid"          → la invitación no existe, ya fue usada o expiró
 */
export type InvitationPreviewMode =
  | "signup-required"
  | "login-required"
  | "ready"
  | "wrong-account"
  | "invalid";

export interface InvitationPreviewResponse {
  mode: InvitationPreviewMode;
  /** Nombre de la organización destino (cuando la invitación es válida) */
  orgName?: string;
  /** Nombre del usuario que invitó (cuando disponible) */
  inviterName?: string;
  /** Si el modo es "signup-required": true si el backend exige password setup */
  requiresPassword?: boolean;
  /** Si el modo es "wrong-account": email de la sesión activa */
  currentEmail?: string;
}

export interface AcceptSignupResponse {
  ok: true;
  /** Ruta a la que navegar tras el signup exitoso */
  redirectTo: string;
}

export interface LoginAndAcceptResponse {
  mode: "magic-link-sent";
}

// ---------------------------------------------------------------------------
// Tipos de errores del dominio de invitaciones
// ---------------------------------------------------------------------------

/**
 * Códigos de error que puede devolver el worker en operaciones de aceptación.
 *
 * - "INVALID_CODE"    → acceptCode incorrecto (403)
 * - "BLOCKED"         → invitación bloqueada por demasiados intentos (403)
 * - "ALREADY_MEMBER"  → el usuario ya es miembro de la organización (409)
 * - "ACCOUNT_EXISTS"  → el email ya tiene cuenta (409)
 * - "INVALID_INPUT"   → body inválido / falta campo requerido (422)
 */
export type InvitationErrorCode =
  | "INVALID_CODE"
  | "BLOCKED"
  | "ALREADY_MEMBER"
  | "ACCOUNT_EXISTS"
  | "INVALID_INPUT";

export class InvitationError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: InvitationErrorCode,
  ) {
    super(message);
    this.name = "InvitationError";
  }
}

// ---------------------------------------------------------------------------
// Helpers internos
// ---------------------------------------------------------------------------

async function toInvitationError(res: Response, fallback: string): Promise<InvitationError> {
  const payload = (await res.json().catch(() => ({}))) as {
    error?: string;
    code?: string;
  };
  return new InvitationError(
    res.status,
    payload.error ?? fallback,
    payload.code as InvitationErrorCode | undefined,
  );
}

// ---------------------------------------------------------------------------
// Funciones públicas
// ---------------------------------------------------------------------------

/**
 * POST /api/auth/invitation/preview — determina el modo de aceptación.
 *
 * No requiere sesión. El worker devuelve el modo basándose en si el email
 * de la invitación tiene cuenta y si hay sesión activa.
 */
export async function previewInvitation(invitationId: string): Promise<InvitationPreviewResponse> {
  const res = await invitationPost("/api/auth/invitation/preview", { invitationId });
  if (!res.ok) {
    throw await toInvitationError(res, "Error al cargar la invitación");
  }
  return (await res.json()) as InvitationPreviewResponse;
}

/**
 * POST /api/auth/invitation/accept-signup — crea cuenta + acepta invitación.
 *
 * Flujo RAMA A: email sin cuenta existente. El backend crea el usuario,
 * genera la sesión (cookie) y acepta la invitación en una sola transacción.
 *
 * Errores posibles:
 *   - 403 INVALID_CODE / BLOCKED → código incorrecto o invitación bloqueada
 *   - 409 ACCOUNT_EXISTS → el email ya tiene cuenta (race condition)
 *   - 422 INVALID_INPUT → falta name/password/acceptCode o no cumplen longitud mínima
 */
export async function acceptSignup(params: {
  invitationId: string;
  acceptCode: string;
  name: string;
  password: string;
}): Promise<AcceptSignupResponse> {
  const res = await invitationPost("/api/auth/invitation/accept-signup", {
    invitationId: params.invitationId,
    acceptCode: params.acceptCode,
    name: params.name,
    password: params.password,
  });
  if (!res.ok) {
    throw await toInvitationError(res, "Error al crear la cuenta");
  }
  return (await res.json()) as AcceptSignupResponse;
}

/**
 * POST /api/auth/invitation/login-and-accept — envía magic link para aceptar.
 *
 * Flujo RAMA B: email con cuenta existente pero sin sesión activa. El backend
 * envía un magic link al email del invitado. Al hacer clic, la sesión se crea
 * y la invitación se acepta automáticamente.
 *
 * Errores posibles:
 *   - 403 INVALID_CODE / BLOCKED → código incorrecto o invitación bloqueada
 *   - 422 INVALID_INPUT → falta invitationId o acceptCode
 */
export async function loginAndAccept(params: {
  invitationId: string;
  acceptCode: string;
}): Promise<LoginAndAcceptResponse> {
  const res = await invitationPost("/api/auth/invitation/login-and-accept", {
    invitationId: params.invitationId,
    acceptCode: params.acceptCode,
  });
  if (!res.ok) {
    throw await toInvitationError(res, "Error al enviar el enlace de acceso");
  }
  return (await res.json()) as LoginAndAcceptResponse;
}

/**
 * POST /api/auth/invitation/accept-now — acepta invitación con sesión activa.
 *
 * Flujo RAMA C: el usuario ya tiene sesión activa y el email coincide con la
 * invitación. Solo necesita el código de aceptación para confirmar.
 *
 * Errores posibles:
 *   - 403 INVALID_CODE / BLOCKED → código incorrecto o invitación bloqueada
 *   - 409 ALREADY_MEMBER → ya es miembro de la organización
 *   - 422 INVALID_INPUT → falta invitationId o acceptCode
 */
export async function acceptNow(params: {
  invitationId: string;
  acceptCode: string;
}): Promise<void> {
  const res = await invitationPost("/api/auth/invitation/accept-now", {
    invitationId: params.invitationId,
    acceptCode: params.acceptCode,
  });
  if (!res.ok) {
    throw await toInvitationError(res, "Error al confirmar la invitación");
  }
}
